import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import placeOrderForApproval from "@insite/client-framework/Store/Pages/CheckoutReviewAndSubmit/Handlers/PlaceOrderForApproval";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import { ErrorContext } from "@tKgpco/Overrides/Pages/CheckoutReviewAndSubmitPage";
import CustomApplicationState from "@tKgpco/Store/CustomApplicationState";
import setCustomerReference1Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference1ErrorHandler";
import setCustomerReference2Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference2ErrorHandler";
import setCustomerReference3Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference3ErrorHandler";
import setCustomerReference4Error from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/CustomerReference4ErrorHandler";
import setExpeditedUserNameError from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/ExpeditedUserNameErrorHandler";
import setExpeditedUserPhoneError from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/ExpeditedUserPhoneErrorHandler";
import setPONumberError from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/PONumberErrorHandler";
import setWorkOrderError from "@tKgpco/Store/Data/CheckoutReviewAndSubmit/Handlers/WorkOrderErrorHandler";
import updateCart from "@tKgpco/Store/Data/CheckoutShipping/Handlers/UpdateCart";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    extendedStyles?: ButtonPresentationProps;
}

const mapStateToProps = (state: CustomApplicationState) => {
    const { isPlacingOrder } = state.pages.checkoutReviewAndSubmit;
    const cartStateCollect = getCurrentCartState(state).value;
    const { session } = state.context;
    const billTo = getBillToState(state, session.billToId).value;
    const checkoutReviewState = state.KGPState.CheckoutReview;
    const billToState = getBillToState(state, cartStateCollect?.billToId || undefined);
    const billToProperties = billToState.value?.properties;

    return {
        isDisabled:
            isPlacingOrder ||
            !checkoutReviewState.expeditedUserName ||
            !checkoutReviewState.expeditedUserphone ||
            !checkoutReviewState.poNumber ||
            !checkoutReviewState.paymentMethod ||
            (billToProperties?.customerOrderReferenceRequired1?.toLowerCase() === "true" &&
                !checkoutReviewState.customerReference1) ||
            (billToProperties?.customerOrderReferenceRequired2?.toLowerCase() === "true" &&
                !checkoutReviewState.customerReference2) ||
            (billToProperties?.customerOrderReferenceRequired3?.toLowerCase() === "true" &&
                !checkoutReviewState.customerReference3) ||
            (billToProperties?.customerOrderReferenceRequired4?.toLowerCase() === "true" &&
                !checkoutReviewState.customerReference4),
        orderApprovalDetailsLink: getPageLinkByPageType(state, "OrderApprovalDetailsPage"),
        cartStatePrpoerties: cartStateCollect?.properties,
        deliveryTerms: billTo?.properties?.deliveryTerms || "",
        collectValue: state?.KGPState?.CheckoutReview.accountNumber || "",
        billToState,
        workOrder: state.KGPState.CheckoutReview.workOrder,
        customerReference1: state.KGPState.CheckoutReview.customerReference1,
        customerReference2: state.KGPState.CheckoutReview.customerReference2,
        customerReference3: state.KGPState.CheckoutReview.customerReference3,
        customerReference4: state.KGPState.CheckoutReview.customerReference4,
        checkoutPoNumber: state.KGPState.CheckoutReview.poNumber,
        expeditedUserName: state.KGPState.CheckoutReview.expeditedUserName,
        expeditedPhone: state.KGPState.CheckoutReview.expeditedUserphone,
        cart: cartStateCollect,
        nextApproverPresent: cartStateCollect?.properties?.nextApproverPresent?.toLowerCase() === "true",
        userRoles: state.context.session.userRoles,
    };
};

const mapDispatchToProps = {
    placeOrderForApproval,
    updateCart,
    setCustomerReference1Error,
    setCustomerReference2Error,
    setCustomerReference3Error,
    setCustomerReference4Error,
    setWorkOrderError,
    setExpeditedUserPhoneError,
    setExpeditedUserNameError,
    setPONumberError,
};

export interface SubmitForApprovalButtonStyles {
    submitForApprovalButton?: ButtonPresentationProps;
}

export const submitForApprovalButtonStyles: SubmitForApprovalButtonStyles = {};

const styles = submitForApprovalButtonStyles;

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    HasToasterContext;

const CheckoutReviewAndSubmitSubmitForApprovalButton = ({
    isDisabled,
    placeOrderForApproval,
    orderApprovalDetailsLink,
    history,
    toaster,
    extendedStyles,
    updateCart,
    cartStatePrpoerties,
    deliveryTerms,
    collectValue,
    cart,
    checkoutPoNumber,
    setPONumberError,
    billToState,
    customerReference1,
    customerReference2,
    customerReference3,
    customerReference4,
    expeditedPhone,
    expeditedUserName,
    setCustomerReference1Error,
    setCustomerReference2Error,
    setCustomerReference3Error,
    setCustomerReference4Error,
    setWorkOrderError,
    setExpeditedUserNameError,
    setExpeditedUserPhoneError,
    workOrder,
    nextApproverPresent,
    userRoles,
}: Props) => {
    const [styles] = useState(() => mergeToNew(submitForApprovalButtonStyles.submitForApprovalButton, extendedStyles));

    const { state, updateError } = useContext(ErrorContext);
    const [properties, setProperties] = useState(billToState.value?.properties);
    const [isRequisitioner, setIsRequisitioner] = React.useState(false);
    // Disallowed special characters
    const forbiddenChars = /[^a-zA-Z0-9\s]/;
    // Check for overly long spacing (more than one consecutive space)
    const excessiveSpacing = / {2,}/;

    React.useEffect(() => {
        if (userRoles && userRoles.length > 0) {
            if (userRoles.includes("Requisitioner") || userRoles.includes("Buyer1")) {
                setIsRequisitioner(true);
            } else {
                setIsRequisitioner(false);
            }
        }
    }, [userRoles]);

    // const handleClick = () => {
    //     const deliveryTerm = deliveryTerms || "";
    //     const hasCollectAccount = cartStatePrpoerties?.hasOwnProperty("collectAccount");

    //     // condition: collect or Third Party.
    //     if (!collectValue && (deliveryTerm.toLowerCase() === "col" || deliveryTerm.toLowerCase() === "n")) {
    //         if (!hasCollectAccount) {
    //             return updateError("collectAccountErrors", true);
    //         }
    //     } else {
    //         updateCart({
    //             onSuccess: () => {
    //                 placeOrderForApproval({
    //                     onSuccess: (cartId: string) => {
    //                         if (orderApprovalDetailsLink) {
    //                             history.push(`${orderApprovalDetailsLink.url}?cartId=${cartId}`);
    //                         } else {
    //                             toaster.addToast({
    //                                 body: translate("Order approval details page doesn't exist."),
    //                                 messageType: "danger",
    //                             });
    //                         }
    //                     },
    //                     onComplete(resultProps) {
    //                         if (resultProps.apiResult?.cart.id) {
    //                             // "this" is targeting the object being created, not the parent SFC
    //                             this.onSuccess?.(resultProps.apiResult.cart.id);
    //                         }
    //                     },
    //                 });
    //             },
    //         });
    //     }
    // };

    const scrollToFirstFormError = () => {
        const firstErrorElement = document.querySelector("[aria-invalid='true']");
        if (firstErrorElement) {
            const formFieldParent = firstErrorElement.closest("[class*='FormFieldStyle']");
            if (!formFieldParent) {
                return;
            }
            const { top } = formFieldParent.getBoundingClientRect();
            window.scrollTo({
                top: top - 150 + window.pageYOffset,
                left: 0,
                behavior: "smooth",
            });
        }
    };

    const validateWorkOrder = () => {
        const error =
            workOrder === "" ? `${siteMessage(properties?.customerOrderReferenceLabel ?? "")} is required` : "";
        setWorkOrderError({ workOrderError: error });
        // setWorkOrderErr(error);
        return error;
    };

    const validateCustomerReference1 = () => {
        const error =
            customerReference1 === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel1 ?? "")} is required`
                : "";
        setCustomerReference1Error({ customerReference1Error: error });
        // setCustomerReference1ValError(error);
        return error;
    };

    const validateCustomerReference2 = () => {
        const error =
            customerReference2 === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel2 ?? "")} is required`
                : "";
        setCustomerReference2Error({ customerReference2Error: error });
        // setCustomerReference2ValError(error);
        return error;
    };

    const validateCustomerReference3 = () => {
        const error =
            customerReference3 === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel3 ?? "")} is required`
                : "";
        setCustomerReference3Error({ customerReference3Error: error });
        // setCustomerReference3ValError(error);
        return error;
    };

    const validateCustomerReference4 = () => {
        const error =
            customerReference4 === ""
                ? `${siteMessage(properties?.customerOrderReferenceLabel4 ?? "")} is required`
                : "";
        setCustomerReference4Error({ customerReference4Error: error });
        // setCustomerReference4ValError(error);
        return error;
    };

    const validatePONumber = () => {
        const error = !checkoutPoNumber || checkoutPoNumber.trim() === "" ? `PO Number is required` : "";
        setPONumberError({ poNumberError: error });
        // setPONumberValError(error);
        return error;
    };

    const validateExpeditedUserName = () => {
        const error = !expeditedUserName
            ? `Name is required`
            : forbiddenChars.test(expeditedUserName)
              ? `Special Characters Not Allowed`
              : excessiveSpacing.test(expeditedUserName)
                ? `Excessive Spacing Not Allowed`
                : "";
        setExpeditedUserNameError({ expeditedUserNameError: error });

        return error;
    };

    const validateExpeditedUserPhone = () => {
        const trimmedPhone = expeditedPhone?.split("-")?.join("");
        const error = !trimmedPhone
            ? `Phone Number is required`
            : !Number(trimmedPhone) || trimmedPhone.length !== 10
              ? `Enter Valid Phone Number`
              : "";
        setExpeditedUserPhoneError({ expeditedUserPhoneError: error });

        return error;
    };

    const handleClick = () => {
        let workOrderValid = false;
        let customerReference1Valid = false;
        let customerReference2Valid = false;
        let customerReference3Valid = false;
        let customerReference4Valid = false;
        let poNumberValid = false;
        const expeditedUserNameValid = validateExpeditedUserName() !== "";
        const expeditedUserPhoneValid = validateExpeditedUserPhone() !== "";

        if (properties?.customerOrderReferenceRequired?.toLowerCase() === "true") {
            workOrderValid = validateWorkOrder() !== "";
        }
        if (
            properties?.customerOrderReferenceRequired1?.toLowerCase() === "true" &&
            properties?.customerOrderReferenceAttribute1 !== "None"
        ) {
            customerReference1Valid = validateCustomerReference1() !== "";
        }

        if (
            properties?.customerOrderReferenceRequired2?.toLowerCase() === "true" &&
            properties?.customerOrderReferenceAttribute2 !== "None"
        ) {
            customerReference2Valid = validateCustomerReference2() !== "";
        }

        if (
            properties?.customerOrderReferenceRequired3?.toLowerCase() === "true" &&
            properties?.customerOrderReferenceAttribute3 !== "None"
        ) {
            customerReference3Valid = validateCustomerReference3() !== "";
        }

        if (
            properties?.customerOrderReferenceRequired4?.toLowerCase() === "true" &&
            properties?.customerOrderReferenceAttribute4 !== "None"
        ) {
            customerReference4Valid = validateCustomerReference4() !== "";
        }

        // if (!requestedDeliveryDateDisabled && !requestedDeliveryDate) {
        //     requestDeliveryDateInValid = true;
        // }

        if (cart?.requiresPoNumber) {
            poNumberValid = validatePONumber() !== "";
        }

        if (
            poNumberValid ||
            workOrderValid ||
            customerReference1Valid ||
            customerReference2Valid ||
            customerReference3Valid ||
            customerReference4Valid ||
            expeditedUserNameValid ||
            expeditedUserPhoneValid
        ) {
            scrollToFirstFormError();
            return;
        }

        placeOrderForApproval({
            onSuccess: (cartId: string) => {
                if (orderApprovalDetailsLink) {
                    history.push(`${orderApprovalDetailsLink.url}?cartId=${cartId}`);
                } else {
                    toaster.addToast({
                        body: translate("Order approval details page doesn't exist."),
                        messageType: "danger",
                    });
                }
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.cart.id) {
                    // "this" is targeting the object being created, not the parent SFC

                    this.onSuccess?.(resultProps.apiResult.cart.id);
                }
            },
        });
    };

    return (
        <Button
            type="submit"
            disabled={isDisabled}
            data-test-selector="checkoutReviewAndSubmit_submitForApproval"
            {...styles}
            onClick={handleClick}
        >
            {isRequisitioner
                ? translate("Submit For Approval")
                : nextApproverPresent
                  ? translate("Submit For Next Approval")
                  : translate("Submit For Approval")}
        </Button>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withToaster(withHistory(CheckoutReviewAndSubmitSubmitForApprovalButton)));
